import { Service, slugify } from '../../data/servicesData';
import { Link } from 'react-router-dom';

interface ServiceDetailModalProps {
  service: Service;
  onClose: () => void;
}

/**
 * ServiceDetailModal Component
 * 
 * Renders a modal ("popout card") displaying a summary of a specific service.
 * Triggered when a user clicks a service card on the main Services page (`src/pages/Services.tsx`).
 * Provides a brief overview and a link to the full detailed service page.
 * 
 * @param service - The Service object containing data for the selected service.
 * @param onClose - Function to close the modal (typically clears the selectedService state in the parent).
 */
export default function ServiceDetailModal({ service, onClose }: ServiceDetailModalProps) {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal Container - Mobile optimized */}
      <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4">
        <div 
          className="relative transform overflow-hidden bg-white sm:bg-premium-dark rounded-t-xl sm:rounded-lg shadow-xl transition-all w-full sm:max-w-2xl max-h-[90vh] flex flex-col animate-fadeIn"
          onClick={(e) => e.stopPropagation()}
          style={{
            boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
          }}
        >
          {/* Modal Header - Mobile Optimized */}
          <div className="p-4 sm:p-6 border-b border-gray-200 sm:border-white/10 flex items-center justify-between bg-white sm:bg-transparent">
            <div className="flex items-center gap-2 sm:gap-3 flex-1 pr-2">
              <span className="text-2xl sm:text-3xl">{service.icon}</span>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 sm:text-white truncate">{service.title}</h3>
            </div>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 sm:hover:text-white transition-colors p-2 sm:p-1 hover:bg-gray-100 sm:hover:bg-transparent rounded-lg"
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        
          {/* Modal Body - Let it grow and scroll */}
          <div className="p-4 sm:p-6 overflow-y-auto flex-grow bg-white sm:bg-transparent">
            <p className="text-gray-700 sm:text-gray-300 mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base">
              {service.details || service.description}
            </p>
            
            {service.benefits && (
              <div className="mb-4 sm:mb-6">
                <h4 className="text-gray-900 sm:text-white font-medium mb-2 sm:mb-3 text-base sm:text-lg">Key Benefits</h4>
                <ul className="space-y-2">
                  {service.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-2 text-gray-600 sm:text-gray-300 text-sm sm:text-base">
                      <span className="text-blue-600 sm:text-blue-400 mt-0.5 sm:mt-1 text-sm sm:text-base">✓</span>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          
          {/* REMOVED Suitability Rating Section */}
          {/* 
          <div className="bg-premium-darker/50 rounded-lg p-3 md:p-4 mb-4 md:mb-6 border border-white/10">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-white font-medium mb-0.5 md:mb-1 text-sm md:text-base">Suitability Rating</h4>
                <p className="text-xs md:text-sm text-gray-400">Based on typical client needs</p>
              </div>
              <div className="flex items-center">
                <span className="text-blue-400 text-base md:text-lg mr-1 md:mr-2">★★★★</span>
                <span className="text-gray-600 text-base md:text-lg">★</span>
              </div>
            </div>
          </div>
          */}
        </div>
        
          {/* Modal Footer - Should not grow */}
          <div className="p-4 sm:p-6 border-t border-gray-200 sm:border-white/10 bg-gray-50 sm:bg-transparent flex flex-col sm:flex-row gap-3 sm:gap-4 justify-between items-center">
            <p className="text-xs sm:text-sm text-gray-600 sm:text-gray-400 text-center sm:text-left">
              Want to learn more about how {service.title} can benefit you?
            </p>
            <div className="flex gap-2 sm:gap-3 w-full sm:w-auto">
              <button 
                onClick={onClose}
                className="flex-1 sm:flex-initial px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-md border border-gray-300 sm:border-white/20 hover:bg-gray-100 sm:hover:bg-white/10 text-gray-700 sm:text-white transition-colors bg-white sm:bg-transparent"
              >
                Close
              </button>
              {/* "Learn More" button links to the full, detailed service page */}
              {/* It uses the slugify utility to create the correct URL path (e.g., /services/term-life-insurance) */}
              <Link
                to={`/services/${slugify(service.title)}`}
                className="flex-1 sm:flex-initial px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-md bg-gradient-to-r from-blue-500 to-purple-600 text-white transition-all hover:opacity-90 text-center shadow-sm"
              >
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
